import { useCallback, useRef, useState } from "react";
import {
	urlParam,
	useDropUnknownValue,
	useUrlState,
} from "@/shared/hooks/useUrlState";
import {
	Autocomplete,
	Box,
	Button,
	CircularProgress,
	Paper,
	TextField,
	Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import { AgGridReact } from "ag-grid-react";
import type { ColDef, GridApi, GridReadyEvent } from "ag-grid-community";
import { ErrorModal } from "@dexteel/mesf-core";
import { useRollsInUse, useStands } from "../hooks/useRollsInUse";
import type { RollInUseRow } from "../models/RollsInUse";

const num = (dec: number) => (p: { value: unknown }) =>
	p.value == null || p.value === "" ? "" : Number(p.value).toFixed(dec);

const COLUMN_DEFS: ColDef<RollInUseRow>[] = [
	{ field: "Rollset", headerName: "Rollset", flex: 1, minWidth: 120 },
	{ field: "Name", headerName: "Roll", flex: 1, minWidth: 120 },
	{ field: "BarrelDescription", headerName: "Barrel", flex: 1, minWidth: 140 },
	{
		field: "Diameter",
		headerName: "Diameter",
		width: 110,
		cellStyle: { textAlign: "right" },
		valueFormatter: num(3),
	},
	{
		field: "Width",
		headerName: "Width",
		width: 100,
		cellStyle: { textAlign: "right" },
		valueFormatter: num(3),
	},
	{
		field: "Hardness",
		headerName: "Hardness",
		width: 100,
		cellStyle: { textAlign: "center" },
	},
	{ field: "MillSize", headerName: "Mill Size", width: 110 },
	{ field: "RollMaterial", headerName: "Material", flex: 1, minWidth: 130 },
	{
		field: "Cost",
		headerName: "Cost",
		width: 110,
		cellStyle: { textAlign: "right" },
		valueFormatter: num(2),
	},
];

// The legacy screen carried the stand in its URL as "stand".
const URL_SCHEMA = { stand: urlParam.int() };

export const RollsInUseReport = () => {
	const [{ stand: standId }, setFilters, { dropValues }] =
		useUrlState(URL_SCHEMA);
	const [errorMsg, setErrorMsg] = useState("");
	const gridApiRef = useRef<GridApi<RollInUseRow> | null>(null);

	const dataQuery = useRollsInUse(standId);
	const standsQuery = useStands();
	useDropUnknownValue(
		standId,
		standsQuery.isSuccess,
		(standsQuery.data ?? []).map((s) => s.Id),
		useCallback(() => dropValues("stand"), [dropValues]),
	);
	const rows = dataQuery.data ?? [];
	const selectedStand =
		(standsQuery.data ?? []).find((s) => s.Id === standId) ?? null;

	const handleGridReady = useCallback((e: GridReadyEvent<RollInUseRow>) => {
		gridApiRef.current = e.api;
	}, []);

	const handleExportExcel = useCallback(() => {
		gridApiRef.current?.exportDataAsExcel({ fileName: "Rolls_In_Use.xlsx" });
	}, []);

	if (dataQuery.error && !errorMsg) {
		setErrorMsg((dataQuery.error as Error).message);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "calc(100vh - 130px)",
				gap: 1,
				p: 1,
				bgcolor: "#f0f2f5",
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600 }}>
				Rolls in Use
			</Typography>

			<Paper
				elevation={3}
				sx={{
					p: 1.5,
					display: "flex",
					gap: 1.5,
					alignItems: "center",
					flexWrap: "wrap",
				}}
			>
				<Autocomplete
					size="small"
					sx={{ minWidth: 220 }}
					options={standsQuery.data ?? []}
					value={selectedStand}
					getOptionLabel={(o) => o.Name}
					isOptionEqualToValue={(o, v) => o.Id === v.Id}
					onChange={(_e, option) => setFilters({ stand: option?.Id ?? null })}
					renderInput={(params) => <TextField {...params} label="Stand" />}
				/>
				<Typography variant="body2" color="text.secondary">
					{rows.length} roll(s)
				</Typography>
				<Box sx={{ flex: 1 }} />
				<Button
					size="small"
					variant="outlined"
					startIcon={<RefreshIcon />}
					onClick={() => dataQuery.refetch()}
					disabled={dataQuery.isFetching}
				>
					Refresh
				</Button>
				<Button
					size="small"
					variant="outlined"
					startIcon={<DownloadIcon />}
					onClick={handleExportExcel}
				>
					Export
				</Button>
			</Paper>

			<Paper elevation={3} sx={{ flex: 1, overflow: "hidden" }}>
				{dataQuery.isLoading ? (
					<Box
						sx={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							height: "100%",
						}}
					>
						<CircularProgress />
					</Box>
				) : (
					<AgGridReact<RollInUseRow>
						rowData={rows}
						columnDefs={COLUMN_DEFS}
						defaultColDef={{ resizable: true, sortable: true }}
						suppressContextMenu
						headerHeight={32}
						rowHeight={28}
						onGridReady={handleGridReady}
					/>
				)}
			</Paper>

			<ErrorModal error={errorMsg} onHide={() => setErrorMsg("")} />
		</Box>
	);
};
